import { customFunctions } from "@/config/customFunction";
import DefaultInstance, { MasterInstance } from "@/instance/axios";
import { dataStyle, headerStyle, buildStyledRows } from "@/utils/excelDownLoad";
import { multiSheetExcelDownload } from "@/utils/multiSheetExcelDownload";
import {
  AbstractItemsGet200ResponseRowsInner,
  AuthSignupPost201ResponseEquipment,
  DefectsGet200ResponseRowsInner,
  DepartmentsGet200ResponseRowsInner,
  DowntimeReasonsGet200ResponseRowsInner,
  GradesGet200ResponseRowsInner,
  ItemCategoriesGet200ResponseRowsInner,
  ItemGradeViewGet200ResponseRowsInner,
  ItemsGet200ResponseRowsInner,
  ItemsGet200ResponseRowsInnerRoutingsInner,
  LocationsGet200ResponseRowsInner,
  MoldsGet200ResponseRowsInner,
  MoldStocksGet200ResponseRowsInner,
  OperationsGet200ResponseRowsInner,
  PartnersGet200ResponseRowsInner,
  RoutingOutsourcesGet200ResponseRowsInner,
  SitesGet200ResponseRowsInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

export const useStandardMultiSheetExcelDownload = (setLoading: (state: boolean) => void) => {
  const toString = (value: any) => {
    return value ? value.toString() : "";
  };

  const { t } = useTranslation();
  const today = dayjs(new Date()).format("YYYYMMDD");

  // 청크 크기 설정
  const CHUNK_SIZE = 5;

  // 청크 처리 함수
  const chunkArray = (array: string | any[], size: number) => {
    return Array.from({ length: Math.ceil(array.length / size) }, (_, index) =>
      array.slice(index * size, index * size + size),
    );
  };

  const downloadExcel = async () => {
    try {
      setLoading(true);
      const apiRequests = {
        items: MasterInstance.itemsFindPost({}),
        equipments: MasterInstance.equipmentsFindPost({ populate: ["works", "location"] }),
        defects: DefaultInstance.defectsFindPost({}),
        operations: DefaultInstance.operationsFindPost({ populate: ["location"] }),
        routings: DefaultInstance.routingsFindPost({ populate: ["item", "operation", "mold"] }),
        sites: MasterInstance.sitesFindPost({}),
        locations: MasterInstance.locationsFindPost({}),
        routingOutsources: DefaultInstance.routingOutsourcesFindPost({ populate: ["location"] }),
        molds: MasterInstance.moldsFindPost({}),
        moldStocks: MasterInstance.moldStocksFindPost({}),
        abstractItems: DefaultInstance.abstractItemsFindPost({}),
        itemCategories: MasterInstance.itemCategoriesFindPost({}),
        downtimeReasons: DefaultInstance.downtimeReasonsFindPost({}),
        ...(customFunctions.ADD_STANDARD_INFO_GRADE_MENU_SETTING && {
          grades: MasterInstance.gradesFindPost({}),
        }),
        ...(customFunctions.ADD_STANDARD_INFO_DEPART_CODE_MENU_SETTING && {
          departments: MasterInstance.departmentsFindPost({}),
        }),
        ...(customFunctions.ADD_STANDARD_INFO_PARTNER_MENU_SETTING && {
          partners: MasterInstance.partnersFindPost({}),
        }),
        ...(customFunctions.ADD_STANDARD_INFO_BOM_SEARCH_MENU_SETTING && {
          bomSearch: DefaultInstance.itemGradeViewFindPost({}),
        }),
      };

      const keys = Object.keys(apiRequests);
      const promises = Object.values(apiRequests);

      // 청크 처리
      const apiChunks = chunkArray(promises, CHUNK_SIZE);
      const resultChunks: any[] = [];

      for (const chunk of apiChunks) {
        const responses = await Promise.allSettled(chunk);
        resultChunks.push(...responses);
        await new Promise((resolve) => setTimeout(resolve, 500)); // 서버 과부하 방지
      }

      // Key별 결과 매핑
      const results: { [key: string]: any } = keys.reduce(
        (acc, key, index) => {
          const response = resultChunks[index];
          acc[key] = response.status === "fulfilled" ? response.value : null;
          return acc;
        },
        {} as { [key: string]: any },
      );

      const {
        items,
        equipments,
        defects,
        operations,
        routings,
        sites,
        locations,
        routingOutsources,
        molds,
        moldStocks,
        abstractItems,
        itemCategories,
        downtimeReasons,
        grades,
        departments,
        partners,
        bomSearch,
      } = results;

      const sheetData = [
        {
          // 경남케미컬은 브랜치에서 따로 작업.
          sheetName: "품목",
          headerRow: [
            { v: t("item.name"), t: "s", s: headerStyle },
            { v: t("item.type"), t: "s", s: headerStyle },
            { v: t("item.code"), t: "s", s: headerStyle },
            { v: t("item.category"), t: "s", s: headerStyle },
            { v: t("abstractItem.code"), t: "s", s: headerStyle },
            { v: t("item.spec"), t: "s", s: headerStyle },
            { v: t("item.height"), t: "s", s: headerStyle },
            { v: t("item.depth"), t: "s", s: headerStyle },
            { v: t("item.width"), t: "s", s: headerStyle },
            { v: t("item.texture"), t: "s", s: headerStyle },
            { v: t("item.standardUph"), t: "s", s: headerStyle },
            { v: t("item.unit"), t: "s", s: headerStyle },
            { v: "로트 관리기준 (로트명)", t: "s", s: headerStyle },
            { v: "로트 관리기준 (유효기한)", t: "s", s: headerStyle },
          ],
          dataRows: buildStyledRows(
            items?.data?.map((item: ItemsGet200ResponseRowsInner) => [
              item.name,
              t(item.itemType),
              item.code,
              item.itemCategoryName,
              item.abstractItemCode,
              item.spec,
              item.height,
              item.depth,
              item.width,
              item.texture,
              item.standardUph,
              item.unitText,
              item.usesLotName,
              item.usesLotExpiration,
            ]) ?? [],
            dataStyle,
          ).styledRows,
        },
        {
          sheetName: "설비",
          headerRow: [
            { v: t("site.code"), t: "s", s: headerStyle },
            { v: t("equipment.code"), t: "s", s: headerStyle },
            { v: t("equipment.name"), t: "s", s: headerStyle },
            { v: t("equipment.type"), t: "s", s: headerStyle },
            { v: t("equipment.volt"), t: "s", s: headerStyle },
            { v: t("equipment.fromLocation"), t: "s", s: headerStyle },
            { v: t("equipment.toLocation"), t: "s", s: headerStyle },
            { v: t("equipment.targetLocation"), t: "s", s: headerStyle },
            ...(customFunctions.ADD_WORKER_ADD_FUNCTION
              ? [{ v: t("equipment.workerGroup"), t: "s", s: headerStyle }]
              : []),
            { v: t("equipment.downtimeReason"), t: "s", s: headerStyle },
          ],
          dataRows: buildStyledRows(
            equipments.data?.map((equipment: AuthSignupPost201ResponseEquipment) => [
              equipment.siteCode,
              equipment.code,
              equipment.name,
              equipment.equipmentType,
              equipment.volt,
              equipment.fromLocationName,
              equipment.toLocationName,
              equipment.targetLocationName,
            ...(customFunctions.ADD_WORKER_ADD_FUNCTION
                ? [equipment.workerGroup?.join(", ")]
              : []),
              equipment.downtimeReasonCode,
            ]) ?? [],
            dataStyle,
          ).styledRows,
        },
        {
          sheetName: "불량",
          headerRow: [
            { v: t("defect.code"), t: "s", s: headerStyle },
            { v: t("defect.name"), t: "s", s: headerStyle },
            { v: t("operation.code"), t: "s", s: headerStyle },
          ],
          dataRows: buildStyledRows(
            defects?.data?.map((defect: DefectsGet200ResponseRowsInner) => [
              defect.code,
              defect.name,
              defect.operationCode,
            ]) ?? [],
            dataStyle,
          ).styledRows,
        },
        {
          sheetName: "공정",
          headerRow: [
            { v: t("operation.code"), t: "s", s: headerStyle },
            { v: t("operation.name"), t: "s", s: headerStyle },
            { v: t("site.code"), t: "s", s: headerStyle },
            { v: t("operation.fromLocationName"), t: "s", s: headerStyle },
            { v: t("operation.toLocationName"), t: "s", s: headerStyle },
          ],
          dataRows: buildStyledRows(
            operations?.data?.map((operation: OperationsGet200ResponseRowsInner) => [
              operation.code,
              operation.name,
              operation.siteCode,
              operation.fromLocationName,
              operation.toLocationName,
            ]) ?? [],
            dataStyle,
          ).styledRows,
        },
        {
          sheetName: "라우팅",
          headerRow: [
            { v: t("routing.code"), t: "s", s: headerStyle },
            { v: t("item.name"), t: "s", s: headerStyle },
            { v: t("item.code"), t: "s", s: headerStyle },
            { v: t("item.spec"), t: "s", s: headerStyle },
            { v: t("operation.code"), t: "s", s: headerStyle },
            { v: t("routing.type"), t: "s", s: headerStyle },
            { v: t("mold.code"), t: "s", s: headerStyle },
            { v: t("routing.consumeType"), t: "s", s: headerStyle },
            { v: t("routing.seq"), t: "s", s: headerStyle },
          ],
          dataRows: buildStyledRows(
            routings?.data?.map((routing: ItemsGet200ResponseRowsInnerRoutingsInner) => [
              routing.code,
              routing.item?.name,
              routing.itemCode,
              routing.item?.spec,
              routing.operationCode,
              t(toString(routing.routingType)),
              routing.moldCode,
              t(toString(routing.consumeType)),
              routing.seq,
            ]) ?? [],
            dataStyle,
          ).styledRows,
        },
        {
          sheetName: "사업장",
          headerRow: [
            { v: t("site.code"), t: "s", s: headerStyle },
            { v: t("site.name"), t: "s", s: headerStyle },
          ],
          dataRows: buildStyledRows(
            sites?.data?.map((site: SitesGet200ResponseRowsInner) => [site.code, site.name]) ?? [],
            dataStyle,
          ).styledRows,
        },
        {
          sheetName: "로케이션",
          headerRow: [
            { v: t("location.code"), t: "s", s: headerStyle },
            { v: t("location.name"), t: "s", s: headerStyle },
            { v: t("site.code"), t: "s", s: headerStyle },
            ...(customFunctions.ADD_DEFECT_LOCATION
              ? [{ v: t("location.isAvailable"), t: "s", s: headerStyle }]
              : []),
          ],
          dataRows: buildStyledRows(
            locations?.data?.map((location: LocationsGet200ResponseRowsInner) => [
              location.code,
              location.name,
              location.siteCode,
            ...(customFunctions.ADD_DEFECT_LOCATION
                ? [location.isAvailable ? t("location.able") : t("location.disable")]
              : []),
            ]) ?? [],
            dataStyle,
          ).styledRows,
        },
        {
          sheetName: "외주처",
          headerRow: [
            { v: t("outsource.name"), t: "s", s: headerStyle },
            { v: t("routing.code"), t: "s", s: headerStyle },
            { v: t("outsource.fromLocationName"), t: "s", s: headerStyle },
            { v: t("outsource.toLocationName"), t: "s", s: headerStyle },
          ],
          dataRows: buildStyledRows(
            routingOutsources?.data?.map((outsource: RoutingOutsourcesGet200ResponseRowsInner) => [
              outsource.name,
              outsource.routingCode,
              outsource.fromLocationName,
              outsource.toLocationName,
            ]) ?? [],
            dataStyle,
          ).styledRows,
        },
        {
          sheetName: "금형",
          headerRow: [
            { v: t("mold.code"), t: "s", s: headerStyle },
            { v: t("site.code"), t: "s", s: headerStyle },
            { v: t("mold.capacity"), t: "s", s: headerStyle },
          ],
          dataRows: buildStyledRows(
            molds?.data?.map((mold: MoldsGet200ResponseRowsInner) => [
              mold.code,
              mold.siteCode,
              mold.capacity,
            ]) ?? [],
            dataStyle,
          ).styledRows,
        },
        {
          sheetName: "금형재고",
          headerRow: [
            { v: t("moldStock.code"), t: "s", s: headerStyle },
            { v: t("mold.code"), t: "s", s: headerStyle },
            { v: t("moldStock.limitCounter"), t: "s", s: headerStyle },
            { v: t("moldStock.inspectCounter"), t: "s", s: headerStyle },
            { v: t("moldStock.currentCounter"), t: "s", s: headerStyle },
          ],
          dataRows: buildStyledRows(
            moldStocks?.data?.map((stock: MoldStocksGet200ResponseRowsInner) => [
              stock.code,
              stock.moldCode,
              stock.limitCounter,
              stock.inspectCounter,
              stock.currentCounter,
            ]) ?? [],
            dataStyle,
          ).styledRows,
        },
        {
          sheetName: "품목군",
          headerRow: [{ v: t("abstractItem.code"), t: "s", s: headerStyle }],
          dataRows: buildStyledRows(
            abstractItems?.data?.map((item: AbstractItemsGet200ResponseRowsInner) => [item.code]) ??
              [],
            dataStyle,
          ).styledRows,
        },
        {
          sheetName: "품목분류",
          headerRow: [{ v: t("category.name"), t: "s", s: headerStyle }],
          dataRows: buildStyledRows(
            itemCategories?.data?.map((cat: ItemCategoriesGet200ResponseRowsInner) => [cat.name]) ??
              [],
            dataStyle,
          ).styledRows,
        },
        {
          sheetName: "비가동사유",
          headerRow: [
            { v: t("downtimeReason.seq"), t: "s", s: headerStyle },
            { v: t("downtimeReason.code"), t: "s", s: headerStyle },
            { v: t("downtimeReason.name"), t: "s", s: headerStyle },
            { v: t("operation.code"), t: "s", s: headerStyle },
          ],
          dataRows: buildStyledRows(
            downtimeReasons?.data?.map((item: DowntimeReasonsGet200ResponseRowsInner) => [
              item.seq,
              item.code,
              item.name,
              item.operationCode,
            ]) ?? [],
            dataStyle,
          ).styledRows,
        },
        ...(customFunctions.ADD_STANDARD_INFO_GRADE_MENU_SETTING
          ? [
              {
                sheetName: "등급",
                headerRow: [
                  { v: t("item.gradeCode"), t: "s", s: headerStyle },
                  { v: t("item.gradeName"), t: "s", s: headerStyle },
                ],
                dataRows: buildStyledRows(
                  grades?.data?.map((row: GradesGet200ResponseRowsInner) => [row.code, row.name]) ??
                    [],
                  dataStyle,
                ).styledRows,
              },
            ]
          : []),
        ...(customFunctions.ADD_STANDARD_INFO_DEPART_CODE_MENU_SETTING
          ? [
              {
                sheetName: "부서",
                headerRow: [
                  { v: t("department.code"), t: "s", s: headerStyle },
                  { v: t("department.name"), t: "s", s: headerStyle },
                ],
                dataRows: buildStyledRows(
                  departments?.data?.map((row: DepartmentsGet200ResponseRowsInner) => [
                    row.code,
                    row.name,
                  ]) ?? [],
                  dataStyle,
                ).styledRows,
              },
            ]
          : []),
        ...(customFunctions.ADD_STANDARD_INFO_PARTNER_MENU_SETTING
          ? [
              {
                sheetName: "협력사",
                headerRow: [
                  { v: t("partner.code"), t: "s", s: headerStyle },
                  { v: t("partner.name"), t: "s", s: headerStyle },
                  { v: t("partner.type"), t: "s", s: headerStyle },
                  { v: t("partner.contact"), t: "s", s: headerStyle },
                ],
                dataRows: buildStyledRows(
                  partners?.data?.map((row: PartnersGet200ResponseRowsInner) => [
                    row.code,
                    row.name,
                    t(toString(row.type)),
                    row.contact,
                  ]) ?? [],
                  dataStyle,
                ).styledRows,
              },
            ]
          : []),
        ...(customFunctions.ADD_STANDARD_INFO_BOM_SEARCH_MENU_SETTING
          ? [
              {
                sheetName: "BOM",
                headerRow: [
                  { v: t("item.name"), t: "s", s: headerStyle },
                  ...(customFunctions.ADD_STANDARD_INFO_GRADE_MENU_SETTING
                    ? [{ v: t("item.gradeName"), t: "s", s: headerStyle }]
                    : []),
                  { v: t("item.type"), t: "s", s: headerStyle },
                  { v: t("item.code"), t: "s", s: headerStyle },
                ],
                dataRows: buildStyledRows(
                  bomSearch?.data?.map((row: ItemGradeViewGet200ResponseRowsInner) => [
                    row.name,
                  ...(customFunctions.ADD_STANDARD_INFO_GRADE_MENU_SETTING
                      ? [row.gradeName]
                    : []),
                    t(toString(row.itemType)),
                    row.code,
                  ]) ?? [],
                  dataStyle,
                ).styledRows,
              },
            ]
          : []),
      ];

      await multiSheetExcelDownload({
        sheets: sheetData,
        fileName: `${today}_기준정보`,
      });
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };
  return { downloadExcel };
};
